// =============================================================================
// features/sales/campaigns/operate/components/LeadContextCard.tsx — Coluna
// principal: dados essenciais do lead reservado + roteiro opcional (doc 26
// §6.8 "Coluna principal traz dados essenciais e roteiro opcional").
//
// Roteiro: nenhum slot mesclado em `main` ainda publica um roteiro de
// campanha (não existe endpoint) — mostramos um placeholder explicando a
// ausência em vez de fabricar texto (mesma convenção honesta de
// hooks/sales/useCampaigns.ts "Indisponível").
// =============================================================================

import type { CampaignQueueLeadContext, CampaignQueueMember } from '@elemento/shared-schemas';
import * as React from 'react';

import { useToast } from '../../../../../components/ui/Toast';
import { formatPhoneForDisplay } from '../phone';

export interface LeadContextCardProps {
  lead: CampaignQueueLeadContext;
  member: CampaignQueueMember;
}

export function LeadContextCard({ lead, member }: LeadContextCardProps): React.JSX.Element {
  const { toast } = useToast();
  const phoneDisplay = formatPhoneForDisplay(lead.phone);

  async function copyPhone(): Promise<void> {
    try {
      await navigator.clipboard.writeText(lead.phone);
      toast('Telefone copiado.', 'success');
    } catch {
      toast('Não foi possível copiar automaticamente — selecione o número manualmente.', 'info');
    }
  }

  return (
    <div
      className="flex flex-col gap-5 rounded-md border border-border p-5 sm:p-6"
      style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-2)' }}
    >
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xs font-bold uppercase tracking-[0.08em] text-ink-3">
          Cliente reservado para você
        </p>
        <h2
          className="font-display font-bold text-ink"
          style={{
            fontSize: 'var(--text-2xl)',
            letterSpacing: '-0.04em',
            lineHeight: 1.1,
            fontVariationSettings: "'opsz' 32",
          }}
        >
          {lead.name}
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <p
            className="font-sans font-semibold text-ink-3 uppercase"
            style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
          >
            Telefone
          </p>
          <div className="flex items-center gap-2">
            <span
              className="font-mono text-ink"
              style={{ fontSize: '0.95rem', letterSpacing: '-0.01em' }}
            >
              {phoneDisplay}
            </span>
            <button
              type="button"
              onClick={() => void copyPhone()}
              className="p-1.5 rounded-sm text-ink-3 hover:text-azul hover:bg-surface-hover transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40"
              aria-label="Copiar telefone do cliente"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                className="w-3.5 h-3.5"
                aria-hidden="true"
              >
                <rect x="5.5" y="5.5" width="7" height="8" rx="1.2" />
                <path d="M3.5 10V4.2A1.2 1.2 0 0 1 4.7 3h5.8" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p
            className="font-sans font-semibold text-ink-3 uppercase"
            style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
          >
            Tentativas nesta campanha
          </p>
          <span className="font-mono text-ink text-sm">{member.attemptCount}</span>
        </div>
      </div>

      <div
        className="flex flex-col gap-2 rounded-sm px-4 py-3"
        style={{ background: 'var(--surface-muted)' }}
      >
        <p className="font-sans text-xs font-bold uppercase tracking-[0.08em] text-ink-3">
          Roteiro
        </p>
        <p className="font-sans text-sm text-ink-3 leading-relaxed">
          Nenhum roteiro configurado para esta campanha ainda. Use o histórico ao lado como contexto
          da conversa.
        </p>
      </div>
    </div>
  );
}
